<template>
    <div v-if="properties">
        <!-- Heading / Filters -->
        <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between mb-5">
            <div>
                <h1 class="mb-3">
                    Search Properties
                </h1>
                <p class="text-demibold">
                    Search all My Online properties here to find your new home.
                </p>
            </div>
            <div>
                <router-link
                    :to="{ name: 'saved-properties' }"
                    class="btn btn--primary mb-3"
                >
                    Saved Properties
                </router-link>
                <ul class="list-unstyled mb-0 mt-4 mt-md-0 filter-menu">
                    <li>
                        <a
                            :class="{ active : activeFilter === 'sale' }"
                            href="#"
                            @click.prevent="switchFilter('sale')"
                        >
                            For Sale
                        </a>
                    </li>
                    <li>
                        <a
                            :class="{ active : activeFilter === 'let' }"
                            href="#"
                            @click.prevent="switchFilter('let')"
                        >
                            To Let
                        </a>
                    </li>
                </ul>
            </div>
        </div>
        <!-- END Heading / Filters -->

        <!-- Filter forms -->
        <div
            v-show="activeFilter === 'sale'"
        >
            <b-btn
                v-b-toggle.filter-wrap
                class="filter-properties d-block d-md-none"
            >
                Filter Properties
            </b-btn>
            <b-collapse
                id="filter-wrap"
                visible
            >
                <SearchSaleForm @filterProperties="filterData" />
            </b-collapse>
        </div>
        <div
            v-show="activeFilter === 'let'"
            class="filter-wrap"
        >
            <b-btn
                v-b-toggle.filter-wrap-let
                class="filter-properties d-block d-md-none"
            >
                Filter Properties
            </b-btn>
            <b-collapse
                id="filter-wrap-let"
                visible
            >
                <SearchLetForm @filterPropertiesLet="filterData" />
            </b-collapse>
        </div>
        <!-- END Filter forms -->

        <hr>

        <div class="d-flex align-items-center justify-content-between mb-5">
            <h2>Results</h2>
            <div
                v-if="properties.data && properties.data.length"
                class="d-flex align-items-center"
            >
                <span class="mr-4">{{ properties.from }}-{{ properties.to }} of {{ properties.total }}</span>
                <span class="d-flex align-items-center">
                    <router-link
                        :to="{
                            name: 'search-properties',
                            params: {
                                page: (properties.current_page - 1) > 1 ? properties.current_page - 1 : null
                            },
                            query: validFilters
                        }"
                        :disabled="!prevButtonActive"
                        tag="button"
                        class="btn--inherit"
                    >
                        <SVGSprite
                            :class="{ 'arrow-icon-disabled': !prevButtonActive }"
                            name="arrow-square-left"
                            class="arrow-icon"
                        />
                    </router-link>
                    <router-link
                        :to="{
                            name: 'search-properties',
                            params: {
                                page: properties.current_page + 1
                            },
                            query: validFilters
                        }"
                        :disabled="!nextButtonActive"
                        tag="button"
                        class="btn--inherit"
                    >
                        <SVGSprite
                            :class="{ 'arrow-icon-disabled': !nextButtonActive }"
                            name="arrow-square-right"
                            class="arrow-icon"
                        />
                    </router-link>
                </span>
            </div>
        </div>

        <!-- Properties -->
        <b-row v-if="properties.data && properties.data.length">
            <!-- Property card -->
            <b-col
                v-for="property in properties.data.filter(propertyItem => property.transaction_type === activeFilter)"
                :key="property.id"
                class="mb-5"
                cols="12"
                lg="6"
            >
                <PropertyCard
                    :property="property"
                    :owned="false"
                    :icons="{ save: true, view: true }"
                />
            </b-col>
            <!-- END Property card -->
        </b-row>
        <b-row
            v-else
        >
            <b-col
                cols="12"
            >
                <p>No properties matching your search query were found.</p>
            </b-col>
        </b-row>
        <!-- END Properties -->
    </div>
</template>

<script>
import { pickBy } from 'lodash-es';
import { mapActions, mapState } from 'vuex';
import PropertyCard from '../components/PropertyCard';
import SearchSaleForm from '../components/SearchSaleForm';
import SearchLetForm from '../components/SearchLetForm';

export default {
    name: 'SearchProperties',
    components: {
        PropertyCard,
        SearchSaleForm,
        SearchLetForm
    },
    data() {
        return {
            activeFilter: 'sale',
            transactionTypes: {
                sale: 'Sell',
                rent: 'Let'
            },
            filters: {}
        };
    },
    computed: {
        ...mapState('properties', ['properties']),
        prevButtonActive() {
            return this.properties.current_page > 1;
        },
        nextButtonActive() {
            return this.properties.current_page > 0 && this.properties.current_page < this.properties.last_page;
        },
        validFilters() {
            return pickBy(this.filters, filter => filter !== null);
        }
    },
    mounted() {
        this.getProperties({
            page: this.$route.params.page || 1,
            ...this.$route.query
        });
    },
    methods: {
        ...mapActions('properties', ['getProperties']),
        switchFilter(form) {
            this.activeFilter = form;
        },
        getTransactionType(type) {
            return this.transactionTypes[type] || null;
        },
        filterData(filters) {
            this.filters = filters;
            this.$router.push({
                name: 'search-properties',
                params: {
                    page: this.$route.params.page || 1
                },
                query: this.validFilters
            });
        }
    },
    beforeRouteUpdate(to, from, next) {
        this.getProperties({
            page: to.params.page || 1,
            ...this.validFilters
        })
            .then(() => {
                next();
            });
    }
};
</script>

<style lang="scss">
.btn.filter-properties {
    position: relative;
    width: 100%;
    text-align: left;
    padding: 2rem;
    background: white;
    border: 0;
    color: theme-color('dark-grey');
    font-weight: bold;
    border-top-right-radius: global-var('rounded-corners');
    border-top-left-radius: global-var('rounded-corners');
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;

    &.collapsed {
        border-radius: global-var('rounded-corners') !important;
        transition: border-radius .3s linear .2s;

        &::after {
            transform: translateY(-50%) rotate(-45deg);
        }
    }

    &:hover, &:focus, &:active {
        background: white !important;
        color: theme-color('dark-grey') !important;
        border: 0 !important;
        outline: none !important;
        box-shadow: none !important;
    }

    &::after {
        content: "";
        width: 12px;
        height: 12px;
        background: transparent;
        border-left: 1px solid theme-color('mid-grey');
        border-bottom: 1px solid theme-color('mid-grey');
        position: absolute;
        right: 2rem;
        top: 50%;
        transform: translateY(-50%) rotate(135deg);
        transition: transform .3s;
    }
}
</style>
